import React, { useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useRefertoForm } from './hooks/useRefertoForm';
import { useRefertoAPI } from './hooks/useRefertoAPI';
import { useRefertoData } from './hooks/useRefertoData';
import { getStatElementClass, getTeamStatClass, calculateTeamTotals, validateRefertoData } from './utils/refertoUtils';
import LoadingSpinner from '../match/components/LoadingSpinner';
import './RefertoTablet.css';

const RefertoTablet = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const refertoForm = useRefertoForm();
  const refertoAPI = useRefertoAPI();
  const { loading, error, isEditMode, partitaCorrente, setIsEditMode, setPartitaCorrente } = useRefertoData(searchParams, refertoForm, refertoAPI);
  const autoSaveTimeoutRef = useRef(null);
  const isCreatingRef = useRef(false);

  // Use refs to always have the latest values
  const partitaCorrenteRef = useRef(partitaCorrente);
  const refertoFormRef = useRef(refertoForm);
  const refertoAPIRef = useRef(refertoAPI);
  const setPartitaCorrenteRef = useRef(setPartitaCorrente);

  // Update refs when values change
  useEffect(() => {
    partitaCorrenteRef.current = partitaCorrente;
    refertoFormRef.current = refertoForm;
    refertoAPIRef.current = refertoAPI;
    setPartitaCorrenteRef.current = setPartitaCorrente;
  }, [partitaCorrente, refertoForm, refertoAPI, setPartitaCorrente]);

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      const currentPartita = partitaCorrenteRef.current;
      const currentForm = refertoFormRef.current;
      const currentAPI = refertoAPIRef.current;
      const currentSetPartita = setPartitaCorrenteRef.current;


      // Auto-save works if we have teams loaded
      if (!currentPartita || !currentForm.squadraA || !currentForm.squadraB) {
        return;
      }

      // Prevent concurrent creates - skip if a create is already in progress
      if (!currentPartita.id && isCreatingRef.current) {
        return;
      }

      // Use edition from match data instead of localStorage
      const edizioneSelezionata = currentPartita.torneo;
      if (!edizioneSelezionata) {
        return;
      }

      // Lock before create
      if (!currentPartita.id) {
        isCreatingRef.current = true;
      }

      const result = await currentAPI.autoUpdateMatch({
        partitaCorrente: currentPartita,
        edizioneSelezionata,
        squadraA: currentForm.squadraA,
        squadraB: currentForm.squadraB,
        convertToMatchFormat: currentForm.convertToMatchFormat,
        tipoPartita: currentPartita.tipo || 'girone',
        fasciaPartita: 'prima'
      });

      if (result.success) {
        // If it was a new match that got created, update the partitaCorrente with the new ID
        if (result.isNewMatch && result.data) {
          currentSetPartita({
            ...currentPartita,
            id: result.data,
            squadra1: currentForm.squadraA.id,
            squadra2: currentForm.squadraB.id
          });
        }
      }
      isCreatingRef.current = false;
    }, 1000); // 1 second delay
  }, []); // No dependencies - we use refs to get current values

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleSalvaReferto = async () => {
    const validation = validateRefertoData(refertoForm.refertoData, refertoForm.squadraA, refertoForm.squadraB);

    if (!validation.isValid) {
      enqueueSnackbar(`Errori di validazione: ${validation.errors.join(', ')}`, { variant: 'warning' });
      return;
    }

    // Prevent concurrent creates
    if (!partitaCorrente?.id && isCreatingRef.current) {
      enqueueSnackbar('Salvataggio in corso, attendere...', { variant: 'info' });
      return;
    }

    // Cancel any pending auto-save to avoid races
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Get edition from match data instead of localStorage
    const edizioneSelezionata = partitaCorrente?.torneo;
    if (!edizioneSelezionata) {
      enqueueSnackbar('Nessuna edizione trovata nei dati della partita', { variant: 'error' });
      return;
    }

    // Lock before create
    if (!partitaCorrente?.id) {
      isCreatingRef.current = true;
    }

    const result = await refertoAPI.salvaReferto({
      isEditMode: !!partitaCorrente?.id, // True if updating existing match, false if creating new
      partitaCorrente,
      edizioneSelezionata,
      squadraA: refertoForm.squadraA,
      squadraB: refertoForm.squadraB,
      convertToMatchFormat: refertoForm.convertToMatchFormat,
      tipoPartita: partitaCorrente?.tipo || 'girone',
      fasciaPartita: 'prima'
    });

    isCreatingRef.current = false;

    if (result.success) {
      enqueueSnackbar(result.message, { variant: 'success' });

      // If it was a new match, update the partitaCorrente with the new ID
      if (!partitaCorrente?.id) {
        setPartitaCorrente({
          ...partitaCorrente,
          id: result.data,
          squadra1: refertoForm.squadraA.id,
          squadra2: refertoForm.squadraB.id
        });
      }

      // Reload data after save like MatchEditor
      if (refertoForm.squadraA && refertoForm.squadraB) {
        const existingStats = await refertoAPI.loadExistingStatistics(
          refertoForm.squadraA.id,
          refertoForm.squadraB.id,
          partitaCorrente?.nome
        );

        if (existingStats && existingStats.statistiche) {
          refertoAPI.convertMatchToRefertoFormat(
            existingStats,
            refertoForm.setRefertoData,
            refertoForm.squadraA,
            refertoForm.squadraB
          );
        }
      }
    } else {
      enqueueSnackbar(result.message, { variant: 'error' });
    }
  };

  const handleStatElementClick = (team, section, playerIndex, column) => {
    refertoForm.toggleStatElement(team, section, playerIndex, column);
    debouncedAutoSave();
  };

  const handleTeamStatClick = (team, section, index) => {
    refertoForm.toggleTeamStat(team, section, index);
    debouncedAutoSave();
  };

  const renderPlayerRows = (squadra, teamKey) => {
    if (!squadra || !squadra.giocatori) return null;

    const sortedGiocatori = [...squadra.giocatori].sort((a, b) => a.numero - b.numero);

    return sortedGiocatori.map((giocatore) => (
      <div key={giocatore.idP} className="referto-tablet-player-row">
        <div className="referto-tablet-player-name">
          <span className="referto-tablet-player-number">[{giocatore.numero < 10 ? `0${giocatore.numero}` : giocatore.numero}]   {giocatore.maglia}</span>
        </div>
        
        {/* Mete fatte */}
        <div className="referto-tablet-mete-section">
          {[1,2,3,4,5,6,7,8].map(col => (
            <div
              key={`mete_${col}`}
              className={getStatElementClass(refertoForm.refertoData, teamKey, 'mete', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
              onClick={() => handleStatElementClick(teamKey, 'mete', giocatore.idP, col)}
            >
              {col}
            </div>
          ))}
        </div>

        {/* scalpi buoni */}
        <div className="referto-tablet-scalpi-buoni-section">
          {[1,2,3,4,5,6,7,8].map(col => (
            <div
              key={`scalpi_buoni_${col}`}
              className={getStatElementClass(refertoForm.refertoData, teamKey, 'scalpiBuoni', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
              onClick={() => handleStatElementClick(teamKey, 'scalpiBuoni', giocatore.idP, col)}
            >
              {col}
            </div>
          ))}
        </div>

        {/* scalpi vuoti */}
        <div className="referto-tablet-scalpi-vuoti-section">
          <div className="referto-tablet-scalpi-vuoti-group">
            {[1,2,3].map(col => (
              <div
                key={`scalpi1_${col}`}
                className={getStatElementClass(refertoForm.refertoData, teamKey, 'scalpi1', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
                onClick={() => handleStatElementClick(teamKey, 'scalpi1', giocatore.idP, col)}
              >
                {col}
              </div>
            ))}
          </div>
          <div className="referto-tablet-scalpi-vuoti-group">
            {[1,2,3].map(col => (
              <div
                key={`scalpi2_${col}`}
                className={getStatElementClass(refertoForm.refertoData, teamKey, 'scalpi2', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
                onClick={() => handleStatElementClick(teamKey, 'scalpi2', giocatore.idP, col)}
              >
                {col}
              </div>
            ))}
          </div>
          <div className="referto-tablet-scalpi-vuoti-group">
            {[1,2].map(col => (
              <div
                key={`scalpi3_${col}`}
                className={getStatElementClass(refertoForm.refertoData, teamKey, 'scalpi3', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
                onClick={() => handleStatElementClick(teamKey, 'scalpi3', giocatore.idP, col)}
              >
                {col}
              </div>
            ))}
          </div>
        </div>

        {/* Assist */}
        <div className="referto-tablet-assist-section">
          {[1,2,3,4,5,6,7,8].map(col => (
            <div
              key={`assist_${col}`}
              className={getStatElementClass(refertoForm.refertoData, teamKey, 'assist', giocatore.idP, col, 'referto-tablet-grid-input referto-clickable')}
              onClick={() => handleStatElementClick(teamKey, 'assist', giocatore.idP, col)}
            >
              {col}
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const renderTeamSection = (squadra, teamKey, teamLabel) => {
    const teamTotals = calculateTeamTotals(refertoForm.refertoData, teamKey, squadra.giocatori || []);
    
    return (
      <div className="referto-tablet-team-section">
        <div className="referto-tablet-team-header">
          <div className="referto-tablet-team-title">Squadra {teamLabel}:</div>
          <div className="referto-tablet-team-name">{squadra ? squadra.nome : ''}</div>
          <div className="referto-tablet-fair-play-section">
            <label>Fair play</label>
            <input
              type="number"
              min="0"
              max="10"
              value={refertoForm.refertoData[teamKey].fairPlay}
              onChange={(e) => {
                refertoForm.handleGeneralInputChange(`${teamKey}.fairPlay`, e.target.value);
                debouncedAutoSave();
              }}
            />
          </div>
          <div className="referto-tablet-totals-display">
            <span>Mete: {teamTotals.mete}</span>
            <span>Pieni: {teamTotals.scalpiBuoni}</span>
            <span>Vuoti: {teamTotals.scalpiVuoti}</span>
            <span>Assit: {teamTotals.assist}</span>
            <span>Falli: {teamTotals.falliSquadra}</span>
          </div>
        </div>

        <div className="referto-tablet-grid-container">
          <div className="referto-tablet-grid-header">
            <div className="referto-tablet-player-header">
              <span>n°</span>
              <span>Nome</span>
            </div>
            <div className="referto-tablet-section-header">Mete fatte</div>
            <div className="referto-tablet-section-header">scalpi buoni</div>
            <div className="referto-tablet-section-header">scalpi vuoti</div>
            <div className="referto-tablet-section-header">Assist</div>
          </div>

          <div className="referto-tablet-players-grid">
            {renderPlayerRows(squadra, teamKey)}
          </div>

          <div className="referto-tablet-team-stats">
            <div className="referto-tablet-stat-row">
              <div className="referto-tablet-stat-label">Falli di squadra</div>
              <div className="referto-tablet-stat-grid">
                {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25].map(i => (
                  <div
                    key={i}
                    className={getTeamStatClass(refertoForm.refertoData, teamKey, 'falliSquadra', i, 'referto-tablet-grid-input referto-clickable')}
                    onClick={() => handleTeamStatClick(teamKey, 'falliSquadra', i)}
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Caricamento referto..." />;
  }

  if (error) {
    return (
      <div className="referto-tablet-container">
        <div className="referto-tablet-error">
          <h2>Errore</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')}>Torna all'Admin</button>
        </div>
      </div>
    );
  }

  return (
    <div className="referto-tablet-container">
      
      <div className="referto-tablet-teams-container">
        {renderTeamSection(refertoForm.squadraA, 'squadraA', 'A')}
        {renderTeamSection(refertoForm.squadraB, 'squadraB', 'B')}
      </div>

      <div className="referto-tablet-footer">
        <p>Schema ufficiale per referto incontro di Scoutball, aggiornato al Regolamento del Gioco della Scoutball - Edizione 2025, Proprietà di AdS - Amanti dello Scoutball</p>
      </div>
    </div>
  );
};

export default RefertoTablet;